import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate, Routes, Route } from 'react-router-dom';
import { connect } from 'react-redux';

import { Show } from '../show';
import { exerciseTypes } from 'z-js-commons';

import { useOnlineStatus, useWindowSize } from '../../lib/hooks';
import { getRedirectToSource, getSourceLabel } from '../../lib/utils';

import { END_HOMEWORK_MODAL } from '../../containers/modal/modal-types';
import { HOMEWORK_EXECUTION, PAUSE, RESULTS, START_PAGE } from '../../constants/routes';

import FluidContainer from '../layouts/components/fluid-container';
import ColumnContainer from '../layouts/components/column-container';

import { startHomework, showExerciseCorrection } from '../../actions/status';
import { showModal } from '../../actions/modal';

import Button from '../button';
import StartHomeworkButton from './start-homework-button';
import { SOURCES } from '../../constants/sources';
import { GROUPS } from '../../constants/groups';
import { isCorrectionEnabled } from '../../lib/exercise';
import { currentSectionIsLast } from '../../lib/status';
import { sectionPropType } from '../../lib/prop-types';
import { APPLICATION_MODE } from '../../constants/layout';
import DOMPurify from 'dompurify';

export const getLabel = (exerciseTypeLabel, isMobile) => {
    const exerciseType = exerciseTypes.getExerciseTypeFromName(exerciseTypeLabel);
    if (exerciseType === exerciseTypes.OPEN_ANSWER) {
        return isMobile ? 'Conferma' : 'Conferma esercizio';
    }
    return isMobile ? 'Correggi' : 'Correggi esercizio';
};

const ResultsButton = () => {
    const navigate = useNavigate();
    return (
        <Button type="tertiary" size="small" disabled={false} onClick={() => setTimeout(navigate, 0, RESULTS, { replace: true })}>
            {'Risultati'}
        </Button>
    );
};

export const TerminateHomeworkButton = ({ answers, isMobile, onlineStatus, sections, showModal, statusHttpStatus }) => {
    const disabled = !sections && (answers.every((item) => item.answer.length === 0) || !!statusHttpStatus.errorbody);
    let buttonLabel = 'Termina';

    if (!isMobile) {
        if (!!sections) {
            buttonLabel += currentSectionIsLast(sections) ? ' sezione e prova' : ' sezione';
        } else {
            buttonLabel += ' prova';
        }
    }

    return (
        <Button type="tertiary" size="small" disabled={disabled || !onlineStatus} onClick={() => showModal(END_HOMEWORK_MODAL)}>
            {buttonLabel}
        </Button>
    );
};

export const GotoSourceButton = ({ bookId, source, type, fluid = false, homework, backToHome }) => {
    const sourceLabel = source === SOURCES.GOOGLE_CLASSROOM ? 'Classroom' : getSourceLabel(source);

    const homeworkId = homework?.id;
    const ugc = homework?.ugc;

    return (
        <Button
            as="a"
            disabled={false}
            href={DOMPurify.sanitize(getRedirectToSource(source, bookId, homeworkId, backToHome, ugc))}
            size="small"
            type={type}
            fluid={fluid}
        >
            {`Torna su ${sourceLabel}`}
        </Button>
    );
};

export const GotoHomeworkButton = ({ groupId, source, isAssignment, isMobile }) => {
    let correctionsUrl;
    if (source === SOURCES.TT) {
        correctionsUrl = '/matematicabergamini/#/student/mycopybook';
    } else {
        correctionsUrl = `/correzioni?gotoAssigned=${isAssignment ? 1 : 0}`;
    }

    let label = isMobile ? 'Vai al quaderno' : 'Vai al tuo quaderno';
    if ([GROUPS.FUNCTIONARY, GROUPS.TEACHER, GROUPS.TEACHER_UNI].some((e) => e === groupId)) {
        label = isMobile ? 'Vai alle correzioni' : 'Vai alle tue correzioni';
    }

    return (
        <Button size="small" type="tertiary" as="a" href={correctionsUrl}>
            {label}
        </Button>
    );
};

// Export ha il solo scopo di permettere di testare il componente senza la necessità di uno store
// Da eliminare quando non più necessarie
export const ButtonsBar = ({
    answers = [],
    correctionsStatus,
    exercise,
    finished,
    groupId,
    homework,
    homeworkStatus,
    paused,
    profile,
    resumed,
    sections,
    showExerciseCorrection,
    showModal,
    started,
    statusHttpStatus,
    isOpenAssignment,
    journey,
    mode,
    backToHome,
}) => {
    const onlineStatus = useOnlineStatus();
    const { innerWidth } = useWindowSize();
    const isMobile = innerWidth < 340;
    const { bookId, googleCourseWorkId, isEmbedded: isApplicationEmbedded, source, googleAddon } = profile;

    const isCorrectionDisabled = !isCorrectionEnabled(exercise, answers, correctionsStatus) || !onlineStatus;
    const backSource = !!googleCourseWorkId ? SOURCES.GOOGLE_CLASSROOM : source;
    return (
        <>
            <ColumnContainer backgroundColor="surface-grey-050" boxShadow="shadow-01dp" justifyContent="center">
                <ColumnContainer width={[0, 0, 1 / 12]} />

                <FluidContainer
                    height={56}
                    justifyContent="space-between"
                    alignItems="center"
                    paddingX={['mobile', 'tablet', 'desktop']}
                    style={{ maxWidth: '1152px' }}
                >
                    {/* Left container */}
                    <div>
                        <Show when={profile.status.completed && !homeworkStatus.error}>
                            <Show when={!finished && !started && !paused && !resumed}>
                                <StartHomeworkButton disabled={!onlineStatus} />
                            </Show>
                        </Show>
                        <Routes>
                            <Route
                                path={HOMEWORK_EXECUTION}
                                element={
                                    <Show when={finished}>
                                        <ResultsButton />
                                    </Show>
                                }
                            />
                        </Routes>

                        <Show when={!finished && started}>
                            <Show when={profile.singleAnswer}>
                                <Button
                                    disabled={isCorrectionDisabled}
                                    type="tertiary"
                                    size="small"
                                    onClick={() => {
                                        showExerciseCorrection(
                                            exercise,
                                            answers.find((answer) => answer.exerciseId === exercise.id).answer,
                                        );
                                    }}
                                >
                                    {getLabel(exercise && exercise.typeLabel, isMobile)}
                                </Button>
                            </Show>
                        </Show>

                        <Routes>
                            <Route
                                path={PAUSE}
                                element={
                                    <Show when={groupId && !isApplicationEmbedded && source !== SOURCES.LAZE}>
                                        <GotoHomeworkButton
                                            groupId={groupId}
                                            source={source}
                                            isAssignment={!!profile.assignmentId}
                                            isMobile={isMobile}
                                        />
                                    </Show>
                                }
                            />
                            <Route
                                path={RESULTS}
                                element={
                                    <Show when={groupId && !isApplicationEmbedded && source !== SOURCES.LAZE}>
                                        <GotoHomeworkButton
                                            groupId={groupId}
                                            source={source}
                                            isAssignment={!!profile.assignmentId}
                                            isMobile={isMobile}
                                        />
                                    </Show>
                                }
                            />
                        </Routes>
                    </div>
                    {/* right container */}
                    <div>
                        <Routes>
                            <Route
                                path={PAUSE}
                                element={
                                    <Show when={!journey && !isApplicationEmbedded && !googleAddon}>
                                        <GotoSourceButton
                                            source={backSource}
                                            bookId={bookId}
                                            homework={!isOpenAssignment ? homework : undefined}
                                            type="tertiary"
                                            backToHome={backToHome}
                                        />
                                    </Show>
                                }
                            />
                            <Route
                                path={START_PAGE}
                                element={
                                    <Show when={!journey && !isApplicationEmbedded && !googleAddon}>
                                        <GotoSourceButton
                                            source={backSource}
                                            bookId={bookId}
                                            homework={!isOpenAssignment ? homework : undefined}
                                            type="tertiary"
                                            backToHome={backToHome}
                                        />
                                    </Show>
                                }
                            />
                        </Routes>
                        <Show when={!googleAddon && mode !== APPLICATION_MODE.EMBEDDED && !journey && finished && !isApplicationEmbedded}>
                            <GotoSourceButton
                                source={backSource}
                                bookId={bookId}
                                homework={!isOpenAssignment ? homework : undefined}
                                type="tertiary"
                                backToHome={backToHome}
                            />
                        </Show>
                        <Show when={!finished && started}>
                            <TerminateHomeworkButton
                                answers={answers}
                                isMobile={isMobile && profile.singleAnswer}
                                onlineStatus={onlineStatus}
                                sections={sections}
                                showModal={showModal}
                                statusHttpStatus={statusHttpStatus}
                            />
                        </Show>
                    </div>
                </FluidContainer>

                <ColumnContainer width={[0, 0, 1 / 12]} />
            </ColumnContainer>
        </>
    );
};

TerminateHomeworkButton.propTypes = {
    answers: PropTypes.array,
    isMobile: PropTypes.bool,
    onlineStatus: PropTypes.bool,
    sections: PropTypes.arrayOf(sectionPropType),
    showModal: PropTypes.func,
    statusHttpStatus: PropTypes.object,
};

ResultsButton.propTypes = {
    history: PropTypes.func,
};

GotoSourceButton.propTypes = {
    bookId: PropTypes.number,
    fluid: PropTypes.bool,
    source: PropTypes.string,
    type: PropTypes.string.isRequired,
    backToHome: PropTypes.bool,
    homework: PropTypes.shape({
        id: PropTypes.string,
        ugc: PropTypes.bool,
    }),
};

GotoHomeworkButton.propTypes = {
    groupId: PropTypes.number,
    isAssignment: PropTypes.bool,
    isMobile: PropTypes.bool,
    source: PropTypes.string,
};

ButtonsBar.propTypes = {
    answers: PropTypes.array,
    correctionsStatus: PropTypes.array,
    displayReport: PropTypes.bool,
    exercise: PropTypes.any,
    expiringAssignment: PropTypes.string,
    finished: PropTypes.bool,
    groupId: PropTypes.number,
    homework: PropTypes.shape({
        id: PropTypes.string,
        ugc: PropTypes.bool,
    }),
    homeworkStatus: PropTypes.object,
    onCorrection: PropTypes.func,
    paused: PropTypes.bool,
    profile: PropTypes.object,
    resumed: PropTypes.bool,
    sections: PropTypes.arrayOf(sectionPropType),
    showExerciseCorrection: PropTypes.func,
    showModal: PropTypes.func,
    started: PropTypes.bool,
    statusHttpStatus: PropTypes.object,
    validateCheckerStep: PropTypes.func,
    isOpenAssignment: PropTypes.bool,
    journey: PropTypes.object,
    mode: PropTypes.oneOf([APPLICATION_MODE.LIGHT, APPLICATION_MODE.DEFAULT, APPLICATION_MODE.EMBEDDED, '']),
    backToHome: PropTypes.bool,
};

const stateToProps = (state) => {
    return {
        answers: state.status.answers,
        authStatus: state.auth.status,
        correctionsStatus: state.status.correctionsStatus,
        expiringAssignment: state.status.expiringAssignment,
        finished: state.status.finished,
        groupId: state.auth.groupId,
        homework: state.homework,
        homeworkStatus: state.homework.status,
        paused: state.status.paused,
        profile: state.application,
        resumed: state.status.resumed,
        sections: state.status.sections,
        started: state.status.started,
        statusHttpStatus: state.status.httpStatus,
        isOpenAssignment: state.application.isOpenAssignment,
        journey: state.homework.journey,
        mode: state.application.mode,
        backToHome: state.application.backToHome,
    };
};

const actionCreators = {
    showExerciseCorrection,
    showModal,
};

export default connect(stateToProps, actionCreators)(ButtonsBar);
